import React, { useState } from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const ContactModal = ({ open, onOpenChange }) => {
  const [selectedOffice, setSelectedOffice] = useState(0);

  const offices = [
    {
      id: 1,
      name: "Hyderabad",
      address: "Shivam Tejas Building, 2nd Floor -205, Vayupuri, Sainikpuri",
      city: "Hyderabad, Telangana 500094",
      mapUrl: "https://maps.google.com/?q=Shivam+Tejas+Building+Sainikpuri+Hyderabad"
    },
    {
      id: 2,
      name: "Karimnagar", 
      address: "Xpert Advisor, 2nd floor, Sree Rama Arcade, Maitri Convention, Kisan Nagar",
      city: "Karimnagar 505001",
      mapUrl: "https://maps.google.com/?q=Xpert+Advisor+2nd+floor+Sree+Rama+Arcade+Maitri+Convention+Kisan+Nagar+Karimnagar+505001"
    },
    {
      id: 3,
      name: "Visakhapatnam",
      address: "102 Kailash Appartments, Dutt Island, Siripuram", 
      city: "Visakhapatnam 530003",
      mapUrl: "https://maps.google.com/?q=Kailash+Appartments+Siripuram+Visakhapatnam"
    },
    {
      id: 4,
      name: "Guntur",
      address: "Xpert Advisor Study Abroad, Flat No 3&4 Second Floor, Singapore Plaza, 4/14, 4th Ln, opp. Master Xerox, Brodipet",
      city: "Guntur, Andhra Pradesh 522002",
      phone: "+91 70754 94969",
      mapUrl: "https://maps.google.com/?q=Xpert+Advisor+Study+Abroad+Flat+No+3%264+Second+Floor+Singapore+Plaza+4%2F14+4th+Ln+opp+Master+Xerox+Brodipet+Guntur+Andhra+Pradesh+522002"
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Contact Us
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Offices</h3>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {offices.map((office, index) => (
                <button
                  key={office.id}
                  onClick={() => setSelectedOffice(index)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    selectedOffice === index
                      ? 'bg-brand-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {office.name}
                </button>
              ))}
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-brand-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-1">
                    {offices[selectedOffice].name}
                  </h4>
                  <p className="text-sm text-gray-600 mb-1">
                    {offices[selectedOffice].address}
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {offices[selectedOffice].city}
                  </p>
                </div>
              </div>

              <a
                href={offices[selectedOffice].mapUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="w-full bg-brand-500 hover:bg-brand-600 text-white">
                  Get Directions
                </Button>
              </a>
            </div>

            <div className="mt-6 space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
              
              <a
                href="tel:+917674099636"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100"
              >
                <div className="w-10 h-10 bg-navy-700 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-semibold text-gray-900">+91 7674099636</p>
                </div>
              </a>

              <a
                href="tel:+919059333987"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100"
              >
                <div className="w-10 h-10 bg-navy-700 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-semibold text-gray-900">+91 9059333987</p>
                </div>
              </a>

              <a
                href="mailto:info@xpertadvisor.in"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100"
              >
                <div className="w-10 h-10 bg-brand-500 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-semibold text-gray-900">info@xpertadvisor.in</p>
                </div>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About Us</h3>
            <div className="bg-gray-100 rounded-lg overflow-hidden border border-gray-200" style={{ aspectRatio: '16/9' }}>
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/VQStKMTTXOw"
                title="XpertAdvisor Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
